import * as Sentry from '@sentry/react'

function normalizeValidationDetailMessage(msg: string): string {
	return msg.replace(/^Value error,\s*/i, '').trim()
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return !!value && typeof value === 'object'
}

/**
 * Extract error message from SDK error response
 *
 * Note: 403 errors are already handled globally in initClient.ts error interceptor.
 * This utility extracts messages from other errors that need component-level handling.
 *
 * The SDK throws the parsed JSON response directly when throwOnError is true.
 * For FastAPI, errors are typically in the format: { detail: "..." } or { detail: { message: "..." } }
 */
export function extractErrorMessage(error: unknown, defaultMessage: string): string {
	if (!error) return defaultMessage

	// Handle string errors (network errors, etc.)
	if (typeof error === 'string') {
		return error
	}

	// Handle Error objects
	if (error instanceof Error) {
		return error.message
	}

	// Handle parsed JSON error responses (FastAPI format)
	if (typeof error === 'object') {
		const err = error as {
			detail?:
				| string
				| { message?: string; reason?: string }
				| Array<{ msg?: string; message?: string }>
			message?: string
		}

		// FastAPI error format: { detail: "..." } or { detail: { message: "..." } }
		if (err.detail) {
			if (typeof err.detail === 'string') {
				return err.detail
			}
			if (Array.isArray(err.detail)) {
				const first = err.detail[0]
				const msg = first?.msg ?? first?.message
				if (typeof msg === 'string' && msg.length > 0) {
					return normalizeValidationDetailMessage(msg)
				}
			}
			if (
				typeof err.detail === 'object' &&
				err.detail !== null &&
				!Array.isArray(err.detail) &&
				err.detail.message
			) {
				return err.detail.message
			}
		}

		// Generic error message
		if (err.message) {
			return err.message
		}
	}

	return defaultMessage
}

/**
 * Check if an error was already handled by the error interceptor.
 *
 * The error interceptor handles 403/429 errors with structured error codes
 * and shows appropriate toasts. Component catch blocks should check this
 * before showing their own error toasts to avoid duplicates.
 *
 * @param error - The error object to check
 * @returns true if the error was already handled by the interceptor
 */
export function isErrorHandledByInterceptor(error: unknown): boolean {
	if (!error || typeof error !== 'object' || error instanceof Response) {
		return false
	}

	const err = error as { detail?: { code?: string; message?: string } | string }
	const detail = err.detail

	// Check if it's a structured error with a known error code
	if (typeof detail === 'object' && detail !== null) {
		const errorCode = detail.code
		if (
			errorCode === 'permission_denied' ||
			errorCode === 'license_insufficient' ||
			errorCode === 'license_limit_exceeded' ||
			errorCode === 'account_locked'
		) {
			return true
		}
	}

	return false
}

/** HTTP status from a Hey API / openapi-fetch result when the request has an error (e.g. 409). */
export function getClientResponseStatus(
	result: { response?: { status?: number } } | null | undefined
): number | undefined {
	return result?.response?.status
}

/**
 * Returns false for expected API errors thrown by the generated client when
 * throwOnError is true. The SDK throws the parsed FastAPI JSON body (no HTTP status):
 * - `{ detail: string }` — user-facing HTTPException messages (400/404/500)
 * - `{ detail: [...] }` — FastAPI 422 validation errors
 * Interceptor-handled 403/429 structured errors are also excluded.
 * Real Error instances (network, S3, etc.) still report.
 */
export function shouldReportErrorToSentry(error: unknown): boolean {
	if (!error) {
		return false
	}

	if (error instanceof Error) {
		return true
	}

	if (isErrorHandledByInterceptor(error)) {
		return false
	}

	if (isRecord(error) && 'detail' in error) {
		const { detail } = error as { detail?: unknown }
		if (typeof detail === 'string') {
			return false
		}
		if (Array.isArray(detail) && detail.length > 0) {
			return false
		}
	}

	return true
}

/** Capture to Sentry only when {@link shouldReportErrorToSentry} returns true. */
export function captureUnexpectedError(
	error: unknown,
	captureContext?: Parameters<typeof Sentry.captureException>[1]
): void {
	if (shouldReportErrorToSentry(error)) {
		Sentry.captureException(error, captureContext)
	}
}

/** Associate user feedback with a known upload rejection (expected validation, not a bug). */
export function captureUploadRejectionForFeedback(
	message: string,
	context: {
		rejectionReason: string
		fileExtension: string
		contentType?: string | null
	}
): string | undefined {
	return Sentry.captureMessage(message, {
		level: 'info',
		tags: {
			upload_rejection: context.rejectionReason,
			file_extension: context.fileExtension,
		},
		extra: {
			content_type: context.contentType,
		},
	})
}
